// @flow
import { createSelector, type Selector } from 'reselect';
import Instadate from 'instadate';
import { getOption, type Option } from '../form/select.js';
import { productsSelector, productsListSelector } from '../products/products.selectors.js';
import { resourcesSelector, resourcesListSelector } from '../resource/resource.selectors.js';
import { locationsSelector } from '../location/location.selectors.js';
import {
  transactionsSelector,
  transactionsListSelector
} from '../transaction/transaction.selectors.js';

export const invoiceSelector: Selector<
  ReduxState,
  void,
  $ElementType<ReduxState, 'invoice'>
> = createSelector(
  state => state.invoice,
  invoice => invoice
);

export const invoicesSelector: Selector<
  ReduxState,
  void,
  $ElementType<ReduxState, 'invoices'>
> = createSelector(
  state => state.invoices,
  invoices => invoices
);

export const invoicesListSelector: Selector<ReduxState, void, List> = createSelector(
  state => state.invoicesList,
  invoicesSelector,
  (list, invoices) =>
    (Array.isArray(list) ? [...list] : []).sort((a, b) => invoices[a]?.createdTimestamp - invoices[b]?.createdTimestamp)
);

export const unpaidInvoicesListSelector: Selector<ReduxState, void, List> = createSelector(
  state => state.unpaidInvoicesList,
  invoicesSelector,
  (list, invoices) =>
    (Array.isArray(list) ? [...list] : []).sort((a, b) => invoices[a]?.createdTimestamp - invoices[b]?.createdTimestamp)
);

export const invoiceResourcesSelector: Selector<
  ReduxState,
  void,
  $ElementType<$ElementType<ReduxState, 'invoice'>, 'resources'>
> = createSelector(
  invoiceSelector,
  invoice => invoice.resources || {}
);

export const invoiceResourcesListSelector: Selector<ReduxState, void, List> = createSelector(
  invoiceResourcesSelector,
  resources => Object.keys(resources)
);

export const invoiceProductsSelector: Selector<
  ReduxState,
  void,
  $ElementType<$ElementType<ReduxState, 'invoice'>, 'products'>
> = createSelector(
  invoiceSelector,
  invoice => invoice.products || {}
);

export const invoiceProductsListSelector: Selector<ReduxState, void, List> = createSelector(
  invoiceProductsSelector,
  products => Object.keys(products)
);

export const invoiceProductsOptionsSelector: Selector<ReduxState, void, Option[]> = createSelector(
  productsSelector,
  invoiceProductsSelector,
  productsListSelector,
  (products, invoiceProducts, list) =>
    list.reduce(
      (acc, id) => acc.concat(invoiceProducts[id] ? [] : getOption(products[id].name, id)),
      []
    )
);

export const invoiceResourceOptionsSelector: Selector<ReduxState, void, Option[]> = createSelector(
  resourcesSelector,
  locationsSelector,
  invoiceResourcesSelector,
  resourcesListSelector,
  (resources, locations, invoiceResources, list) => {
    const groupedResources = list.reduce((acc, id) => {
      if (invoiceResources[id]) return acc;

      if (!acc[resources[id].locationID]) {
        acc[resources[id].locationID] = [id];
        return acc;
      }

      acc[resources[id].locationID] = acc[resources[id].locationID].concat(id);
      return acc;
    }, {});

    return Object.keys(groupedResources).reduce(
      (acc, locID) =>
        acc.concat([
          getOption(locations[locID].name, '', { isGroup: true }),
          ...groupedResources[locID].reduce(
            (ac, id) => ac.concat(getOption(resources[id].name, id)),
            []
          )
        ]),
      []
    );
  }
);

export const invoicePaySelector: Selector<
  ReduxState,
  void,
  $ElementType<ReduxState, 'invoicePay'>
> = createSelector(
  state => state.invoicePay,
  invoicePay => invoicePay
);

export const defaultInvoiceSelector: Selector<
  ReduxState,
  void,
  $ElementType<ReduxState, 'defaultInvoice'>
> = createSelector(
  state => state.defaultInvoice,
  defaultInvoice => defaultInvoice
);

export const invoicesTransactionsListSelector: Selector<
  ReduxState,
  void,
  $ElementType<ReduxState, 'defaultInvoice'>
> = createSelector(
  invoicesSelector,
  invoicesListSelector,
  transactionsSelector,
  transactionsListSelector,
  (invoices, invoicesList, transactions, transactionsList) =>
    invoicesList
      .map(invoiceId => ({ type: 'invoice', id: invoiceId }))
      .concat(transactionsList.map(transactionId => ({ type: 'transaction', id: transactionId })))
      .sort((a, b) => {
        const timeA =
          a.type === 'invoice'
            ? invoices[a.id].createdTimestamp
            : transactions[a.id].createdTimestamp;
        const timeB =
          b.type === 'invoice'
            ? invoices[b.id].createdTimestamp
            : transactions[b.id].createdTimestamp;
        return timeA > timeB ? 1 : -1;
      })
);
